import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BellRing, X, Check, ChevronRight, Loader2 } from 'lucide-react';
import { getAlertUnreadCount, getAlertHistoryWithReadState, markAlertsRead } from '../services/api';

/**
 * NotificationBell — top-nav bell with unread badge + dropdown panel.
 *
 * Behaviour:
 *   - Bell icon visible whenever the user is signed in
 *   - Polls /api/account/alert-history/unread-count every 30s for the badge
 *   - Click opens a dropdown that loads the latest alerts (limit 20)
 *   - "Mark all read" sets server lastReadTs to now → badge drops to 0
 *   - Each notification links to /alerts for the full view
 *
 * Backend pattern: lastReadTs per user, any notification with `ts > lastReadTs`
 * is unread. Same pattern GitHub uses — keeps storage tiny and matches user
 * expectations from every other SaaS bell they've used.
 */

const POLL_INTERVAL_MS = 30000;
const SEVERITY_COLOR = {
  critical: 'text-red-400 bg-red-500/10 border-red-500/30',
  warning:  'text-amber-400 bg-amber-500/10 border-amber-500/30',
  info:     'text-blue-400 bg-blue-500/10 border-blue-500/30',
};

function formatTimeAgo(ts) {
  if (!ts) return '';
  const elapsed = Date.now() - new Date(ts).getTime();
  if (elapsed < 60_000) return 'just now';
  if (elapsed < 3_600_000) return `${Math.floor(elapsed / 60_000)}m ago`;
  if (elapsed < 86_400_000) return `${Math.floor(elapsed / 3_600_000)}h ago`;
  if (elapsed < 7 * 86_400_000) return `${Math.floor(elapsed / 86_400_000)}d ago`;
  return new Date(ts).toLocaleDateString();
}

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [marking, setMarking] = useState(false);
  const containerRef = useRef(null);

  // Poll unread count every 30s. Quiet if request fails — we don't want bell
  // errors to scare users; the worst case is the badge is briefly stale.
  const pollUnread = useCallback(async () => {
    try {
      const res = await getAlertUnreadCount();
      const data = res?.data || res;
      setUnreadCount(Number.isFinite(data?.unread) ? data.unread : 0);
    } catch { /* silent — bell stays at last known count */ }
  }, []);

  useEffect(() => {
    pollUnread();
    const id = setInterval(pollUnread, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [pollUnread]);

  // Close on outside-click or Escape
  useEffect(() => {
    if (!open) return undefined;
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    function handleEsc(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  // Load latest alerts when the dropdown opens (don't preload on mount — saves
  // an API call for users who never click the bell).
  async function loadAlerts() {
    setLoading(true);
    try {
      const res = await getAlertHistoryWithReadState({ sinceDays: 7 });
      const data = res?.data || res;
      setAlerts(Array.isArray(data?.alerts) ? data.alerts.slice(0, 20) : []);
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }

  function handleToggle() {
    const next = !open;
    setOpen(next);
    if (next) loadAlerts();
  }

  async function handleMarkAllRead() {
    if (marking || unreadCount === 0) return;
    setMarking(true);
    try {
      await markAlertsRead();
      // Optimistic: set local state immediately so the badge drops without
      // waiting for the next poll tick.
      setUnreadCount(0);
      setAlerts(prev => prev.map(a => ({ ...a, read: true })));
    } catch { /* silent — next poll will reconcile */ }
    finally { setMarking(false); }
  }

  const BellIcon = unreadCount > 0 ? BellRing : Bell;

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={handleToggle}
        title={unreadCount > 0 ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}` : 'Notifications'}
        aria-label="Notifications"
        className={`relative w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
          open ? 'bg-amber-500/15 text-amber-400' : 'text-gray-400 hover:text-white hover:bg-dark-700'
        }`}
      >
        <BellIcon className={`w-5 h-5 ${unreadCount > 0 && !open ? 'animate-pulse' : ''}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border border-dark-800">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-32px)] bg-dark-800 border border-dark-700/60 rounded-xl shadow-2xl shadow-black/40 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-dark-700/60">
            <h3 className="text-white font-bold text-sm">Notifications</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  disabled={marking}
                  className="text-xs text-amber-400 hover:text-amber-300 px-2 py-1 rounded hover:bg-dark-700 flex items-center gap-1 disabled:opacity-50"
                  title="Mark all as read"
                >
                  {marking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="text-gray-500 hover:text-white p-1 rounded hover:bg-dark-700"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 flex items-center justify-center text-gray-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
              </div>
            ) : alerts.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">No notifications yet</p>
                <p className="text-gray-600 text-xs mt-1">Bot fires, trade fills, and platform events will land here.</p>
              </div>
            ) : (
              <ul className="divide-y divide-dark-700/40">
                {alerts.map((a, i) => {
                  const sev = SEVERITY_COLOR[a.severity] || SEVERITY_COLOR.info;
                  return (
                    <li
                      key={a.ts ? `${a.ts}-${i}` : i}
                      className={`px-4 py-3 hover:bg-dark-700/40 transition-colors ${!a.read ? 'bg-amber-500/[0.03]' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        {!a.read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0 mt-1.5" aria-label="Unread"></span>
                        )}
                        <div className={`flex-1 min-w-0 ${a.read ? 'pl-3.5' : ''}`}>
                          <div className="flex items-start justify-between gap-2 mb-0.5">
                            <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${sev}`}>
                              {a.severity || 'info'}
                            </span>
                            <span className="text-[10px] text-gray-500 flex-shrink-0">{formatTimeAgo(a.ts)}</span>
                          </div>
                          <p className="text-white text-sm font-medium leading-snug">{a.title || a.type || 'Notification'}</p>
                          {a.body && (
                            <p className="text-gray-400 text-xs mt-0.5 line-clamp-2 leading-relaxed">{a.body}</p>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          <Link
            to="/alerts"
            onClick={() => setOpen(false)}
            className="flex items-center justify-center gap-1 px-4 py-3 border-t border-dark-700/60 text-amber-400 hover:text-amber-300 hover:bg-dark-700/40 text-xs font-medium transition-colors"
          >
            View all alerts <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      )}
    </div>
  );
}
